import {
  ComplianceExtraFieldDefaultSettingFragment,
  ContactFragment,
  FinancialProjectForSelectFragment,
  LegalEntityForSelectFragment,
  SaleInvoiceFragment,
  SaleInvoiceUpsertSidepanelQuery,
  SalesDocumentTemplateForSelectFragment,
  TaxBridgeActivationFragment,
} from 'generated/resources/graphql'
import moment from 'moment'

import {
  LineItem,
  SaleInvoiceFormFields,
} from 'modules/finance/components/Invoices/SaleInvoicesUpsertSidepanel/SaleInvoiceForm/types'

import {
  buildFormComplianceExtraFieldsWithDefaultValues,
  buildSalesInvoiceFormComplianceExtraFields,
  buildSalesInvoiceFormComplianceExtraFieldsForNewCreditNote,
  findDefaultComplianceExtraFieldsSettingsForLegalEntity,
} from './complianceExtraFields'

type FinanceFinancialDocumentComplianceExtraFieldsConfig = NonNullable<
  SaleInvoiceUpsertSidepanelQuery['finance']['financialDocumentComplianceExtraFieldsConfigs']
>[number]

const createDefaultLineItems = (
  saleInvoice: SaleInvoiceFragment | null | undefined
): LineItem[] => {
  if (saleInvoice && saleInvoice.lineItems.length > 0) {
    return saleInvoice.lineItems.map((item) => ({
      id: item.id,
      description: item.description,
      quantity: Math.abs(item.quantity),
      amount: item.amountCents / 100,
      taxRate: {
        id: item.taxRateId,
        description: undefined,
        rate: item.taxRatePercentage,
        taxType: {
          name: item.taxTypeName ?? '',
          countryCode: item.taxTypeCountry,
        },
      },
    }))
  }

  return []
}

const createDefaultComplianceExtraFields = (
  saleInvoice: SaleInvoiceFragment | null | undefined,
  createCreditNote: boolean,
  complianceExtraFieldsConfigs: FinanceFinancialDocumentComplianceExtraFieldsConfig[],
  legalEntity?: LegalEntityForSelectFragment | null,
  defaultComplianceExtraFieldsSettingsForLegalEntity?: ComplianceExtraFieldDefaultSettingFragment | null
) => {
  const country = (saleInvoice?.legalEntity || legalEntity)?.country

  if (saleInvoice?.complianceFields && saleInvoice.complianceFields.length > 0) {
    if (!createCreditNote) {
      return (
        buildSalesInvoiceFormComplianceExtraFields(
          saleInvoice.complianceFields,
          complianceExtraFieldsConfigs,
          country
        ) ||
        buildFormComplianceExtraFieldsWithDefaultValues(
          complianceExtraFieldsConfigs,
          createCreditNote,
          country,
          defaultComplianceExtraFieldsSettingsForLegalEntity
        )
      )
    } else {
      return buildSalesInvoiceFormComplianceExtraFieldsForNewCreditNote(
        saleInvoice.complianceFields,
        complianceExtraFieldsConfigs,
        country,
        defaultComplianceExtraFieldsSettingsForLegalEntity
      )
    }
  }

  return buildFormComplianceExtraFieldsWithDefaultValues(
    complianceExtraFieldsConfigs,
    createCreditNote,
    country,
    defaultComplianceExtraFieldsSettingsForLegalEntity
  )
}

export const createDefaultFormValues = (
  saleInvoice: SaleInvoiceFragment | null | undefined,
  createCreditNote: boolean,
  defaultCurrency: string | undefined,
  defaultLegalEntity: LegalEntityForSelectFragment | null | undefined,
  contact: ContactFragment | null | undefined,
  financialProject: FinancialProjectForSelectFragment | null | undefined,
  complianceExtraFieldsConfigs: FinanceFinancialDocumentComplianceExtraFieldsConfig[],
  excludeLegalEntityIds?: number[],
  defaultSalesDocumentTemplates?: SalesDocumentTemplateForSelectFragment[],
  defaultComplianceExtraFieldsSettings?: ComplianceExtraFieldDefaultSettingFragment[]
): SaleInvoiceFormFields => {
  const defaultDate = moment().format('YYYY-MM-DD')

  const lineItems = createDefaultLineItems(saleInvoice)

  const defaultComplianceExtraFieldsSettingsForLegalEntity =
    defaultComplianceExtraFieldsSettings && defaultLegalEntity?.id
      ? findDefaultComplianceExtraFieldsSettingsForLegalEntity(
          defaultComplianceExtraFieldsSettings,
          defaultLegalEntity.id
        )
      : undefined

  const complianceExtraFields = createDefaultComplianceExtraFields(
    saleInvoice,
    createCreditNote,
    complianceExtraFieldsConfigs,
    defaultLegalEntity,
    defaultComplianceExtraFieldsSettingsForLegalEntity
  )

  const legalEntity = saleInvoice?.legalEntity || defaultLegalEntity

  const defaultSalesDocumentTemplate = getDefaultSalesDocumentTemplate(
    legalEntity,
    defaultSalesDocumentTemplates
  )

  const footer = !saleInvoice
    ? defaultSalesDocumentTemplate?.footer || undefined
    : saleInvoice?.footer || undefined

  return {
    currency: saleInvoice?.currency || defaultCurrency,
    // Don't copy the sequence when creating a credit note (they use different sequence types now)
    salesInvoiceSequence: createCreditNote
      ? undefined
      : (saleInvoice?.salesInvoiceSequence ?? undefined),
    operationDate: createCreditNote
      ? moment().format('YYYY-MM-DD')
      : saleInvoice?.documentDate || defaultDate,
    issueDate: (createCreditNote ? undefined : saleInvoice?.validatedAt) || undefined,
    dueDate: saleInvoice?.dueDate || undefined,
    legalEntity:
      legalEntity && excludeLegalEntityIds?.includes(legalEntity.id) ? undefined : legalEntity,
    contact: saleInvoice?.vendor || contact,
    lineItems,
    discountPercentage: saleInvoice?.discountPercentage
      ? saleInvoice?.discountPercentage * 100
      : undefined,
    memo: saleInvoice?.memo || undefined,
    footer: footer,
    showMemo: !!saleInvoice?.memo,
    showFooter: !!footer,
    retentionTaxRate: saleInvoice?.retentionTaxRate || undefined,
    project:
      saleInvoice?.projectsFinancialDocumentAssociation?.financialProject || financialProject,
    subproject: saleInvoice?.subprojectsFinancialDocumentAssociation?.subproject || undefined,
    originalFinancialDocumentId: createCreditNote
      ? saleInvoice?.id
      : saleInvoice?.parentFinancialDocument?.id,
    documentType: createCreditNote ? 'credit_note' : saleInvoice?.documentType || 'invoice',
    action: 'saving',
    complianceExtraFields,
    salesDocumentTemplate: !saleInvoice ? defaultSalesDocumentTemplate : undefined,
    exchangeRate: Number(saleInvoice?.exchangeRate) || undefined,
  }
}

export const getDefaultSalesDocumentTemplate = (
  defaultLegalEntity?: LegalEntityForSelectFragment | null | undefined,
  defaultSalesDocumentTemplates?: SalesDocumentTemplateForSelectFragment[]
): SalesDocumentTemplateForSelectFragment | undefined => {
  return defaultSalesDocumentTemplates?.find(
    (template) => template.default && template.legalEntity?.id === defaultLegalEntity?.id
  )
}

export const getLegalEntityToInitialize = (
  activatedTaxBridgeActivations: TaxBridgeActivationFragment[],
  defaultLegalEntity: LegalEntityForSelectFragment | null | undefined,
  excludedSpanishLegalEntityIds: number[],
  spanishLegalEntities: LegalEntityForSelectFragment[],
  nonSpanishLegalEntity: LegalEntityForSelectFragment | null | undefined,
  originPageInvoices: boolean
): LegalEntityForSelectFragment | null | undefined => {
  const defaultId = defaultLegalEntity?.id ?? -1
  // If the default LE is excluded
  if (excludedSpanishLegalEntityIds.includes(defaultId)) {
    if (!originPageInvoices) {
      // Do not set another LE when creating from a project
      return null
    }
    // Prefer the first activated TaxBridge LE if available
    const firstActivatedLEId = activatedTaxBridgeActivations[0]?.legalEntityId
    if (firstActivatedLEId) {
      return spanishLegalEntities.find((le) => le.id === firstActivatedLEId)
    }
    // Fallback to non-Spanish LE
    return nonSpanishLegalEntity
  }
  // Default LE is valid
  return defaultLegalEntity
}
